import {createTrustedTypesPolicy} from './create-policy'
import type {SanitizedHTML} from './safe-html-string'

// The `github-unsafe-static` policy binding is private — `getPolicy` is never
// exported. Unlike `github-sanitize`, this policy has no sanitization gate:
// `createHTML` is the identity function and callers assert the input is
// already verified. `mintUnsafeStaticHTML` is therefore the only mint path, and
// it must only receive HTML that:
// - has known static content (e.g. a validated string literal),
// - comes from a trusted source on the server (e.g. the HTML pipeline), or
// - has already been sanitized by DOMPurify.
//
// For untrusted strings or user-controlled content, do NOT use this policy —
// sanitize first with `@github-ui/sanitize`, which mints through the
// `github-sanitize` policy after running DOMPurify.
const unsafeStaticPolicy = createTrustedTypesPolicy({
  name: 'github-unsafe-static',
  createHTML: input => input,
})

/**
 * Mint an already-verified HTML string as `TrustedHTML` via the
 * `github-unsafe-static` Trusted Types policy, so it can be assigned to a
 * TrustedHTML DOM sink (e.g. `dangerouslySetInnerHTML`) under a
 * `require-trusted-types-for 'script'` CSP without a violation.
 *
 * This is the default sink policy for every non-`TrustedHTML` string rendered
 * by `@github-ui/safe-html`'s `VerifiedHTML` — static literals, server-origin
 * strings, and DOMPurify-sanitized fallback alike — so the name should be read
 * as "verified-but-not-sanitized-at-mint-time", not "static literals only".
 *
 * Falls back to returning the input branded as `SafeHTMLString` when the
 * `trusted_types_policies` flag is off or `window.trustedTypes` is
 * unavailable (e.g. SSR), so behavior is unchanged in those environments.
 *
 * WARNING: the input MUST already be verified — this policy does not sanitize.
 * For untrusted strings or user-controlled content, use `@github-ui/sanitize`
 * (the `github-sanitize` policy) instead, which sanitizes with DOMPurify before
 * minting.
 */
export function mintUnsafeStaticHTML(html: string): SanitizedHTML {
  return unsafeStaticPolicy.mint(html)
}

/**
 * Reset the memoized policy. Test-only — not part of the public package API.
 */
export const _resetUnsafeStaticPolicyForTests = unsafeStaticPolicy._resetForTests

/**
 * Inject a fake `TrustedTypePolicyFactory` (or `null` to simulate the platform
 * global being absent). Test-only — pass `undefined` to revert to the real
 * platform global.
 */
export const _setTrustedTypesForTests = unsafeStaticPolicy._setTrustedTypesForTests
